<template>
  <section v-if="isGetRequestPending" class="edit-skill">
    <p-loading />
  </section>
  <section v-else-if="item || isNew" class="edit-skill">
    <header>
      <div class="header-left">
        <h1>
          <span v-if="comparison">
            Comparing: <span class="skill-name">{{ skillName }}</span>
          </span>
          <span v-else-if="isNew">New Skill</span>
          <span v-else>
            <router-link to="/skills">Skills Library</router-link>
            <span class="separator"> &rsaquo; </span>
            <span class="skill-name">{{ skillName }}</span>
          </span>
        </h1>
        <div v-if="isViewingHistoricalVersion && !comparison" class="version-indicator">
          <p-md-icon name="eye" size="sm" />
          Viewing v{{ item.version }}
          <span class="current-label">(Current: v{{ originalVersion }})</span>
        </div>
      </div>
      <p-button v-if="!isNew && $hasPermission('skills')" color="secondary" class="delete-button" @click="confirmDelete">Delete</p-button>
    </header>
    <div class="content-wrapper">
      <p-compare-skill v-if="comparison" :comparison="comparison" @close="closeComparison" />
      <p-edit-skill-form
        v-else
        :item="item"
        :initial-files="skillFiles"
        :is-request-pending="isUpdateRequestPending"
        :is-request-failed="isUpdateRequestFailed"
        :preview-version="isViewingHistoricalVersion ? item.version : null"
        @submit="submit"
        @cancel="$router.push('/skills')"
      />
      <p-skill-history
        v-if="!isNew"
        :skill-name="skillName"
        :current-version="originalVersion"
        @version-activated="reloadSkill"
        @version-deleted="reloadSkill"
        @compare-version="onCompare"
        @preview-version="onPreview"
      />
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import { cloneDeep } from 'lodash';

import Loading from '@/components/common/Loading';
import Button from '@/components/common/Button';
import MdIcon from '@/components/common/MdIcon';
import EditForm from './_CreateOrEdit';
import SkillHistory from './SkillHistory';
import CompareSkill from './CompareSkill';
import 'jsondiffpatch/lib/formatters/styles/html.css';
import { skillDiffAsHtml } from './diff';

export default {
  components: {
    'p-loading': Loading,
    'p-button': Button,
    'p-md-icon': MdIcon,
    'p-edit-skill-form': EditForm,
    'p-skill-history': SkillHistory,
    'p-compare-skill': CompareSkill
  },
  props: {
    name: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      item: null,
      comparison: null,
      skillName: this.name,
      originalVersion: null
    };
  },
  computed: {
    ...mapState({
      isGetRequestPending: s => s.nlp.getSkillPending,
      isUpdateRequestPending: s => s.nlp.updateSkillPending,
      isUpdateRequestFailed: s => s.nlp.updateSkillFailed,
      skillHistory: s => s.nlp.skillHistory,
      skillFiles: s => s.nlp.skillFiles
    }),
    isNew() {
      return !this.name || this.name === 'new';
    },
    isViewingHistoricalVersion() {
      return this.item && this.originalVersion && this.item.version !== this.originalVersion;
    }
  },
  watch: {
    '$route.query.compareVersion': async function(newVersion) {
      if (newVersion) {
        await this.loadComparison(parseInt(newVersion));
      } else {
        this.comparison = null;
      }
    }
  },
  async created() {
    if (!this.isNew) {
      this.item = await this.$store.dispatch('nlp/getSkill', this.name);
      this.originalVersion = this.item.version;

      await this.$store.dispatch('nlp/getSkillFiles', this.name);

      if (this.$route.query.compareVersion) {
        await this.loadComparison(parseInt(this.$route.query.compareVersion));
      }
    } else {
      this.$store.dispatch('nlp/clearSkillFiles');
    }
  },
  methods: {
    async submit(props) {
      const confirm = await this.$confirm({
        title: `Save new version?`,
        message: `Are you sure you want to save a new version?`,
        confirm: 'Save',
        userResponseLabel: 'Comment'
      });

      if (!confirm) {
        return;
      }

      const action = this.isNew ? 'nlp/createSkill' : 'nlp/updateSkill';
      const payload = {
        skill: {
          ...props.skill,
          message: confirm.userResponse
        },
        stagedFiles: props.stagedFiles,
        newFolders: props.newFolders,
        deletePaths: props.deletePaths
      };
      if (!this.isNew) {
        payload.name = this.skillName;
      }
      try {
        const savedSkill = await this.$store.dispatch(action, payload);

        if (!this.isUpdateRequestFailed) {
          this.$toast.success({
            title: 'Skill saved',
            message: `Skill has been saved successfully.`
          });
          if (this.isNew) {
            const savedName = savedSkill?.name;
            this.skillName = savedName;
            const [skill] = await Promise.all([
              this.$store.dispatch('nlp/getSkill', savedName),
              this.$store.dispatch('nlp/getSkillFiles', savedName)
            ]);
            this.item = skill;
            this.$router.replace(`/skills/${savedName}/edit`);
          } else {
            await this.reloadSkill();
          }
        }
      } catch (e) {
        const serverMessage = await this.extractErrorMessage(e);
        this.$toast.error({
          title: 'Failed to save skill',
          message: serverMessage || 'Please try again later.'
        });
      }
    },
    async reloadSkill() {
      this.item = await this.$store.dispatch('nlp/getSkill', this.skillName);
      this.originalVersion = this.item.version;
      await Promise.all([this.$store.dispatch('nlp/getSkillHistory', this.skillName), this.$store.dispatch('nlp/getSkillFiles', this.skillName)]);
    },
    async onCompare(selectedVersion) {
      await this.$router.push({
        path: this.$route.path,
        query: { compareVersion: selectedVersion.version }
      });
    },
    async onPreview(selectedVersion) {
      if (this.comparison) {
        await this.closeComparison();
      }
      this.item = cloneDeep(selectedVersion);
      await this.$store.dispatch('nlp/getSkillFiles', { name: this.skillName, version: selectedVersion.version });
    },
    closeComparison() {
      this.$router.push({ path: this.$route.path });
    },
    async loadComparison(version) {
      const selectedVersion = this.skillHistory.find(v => v.version === version);

      if (!selectedVersion) {
        return;
      }

      const currentSkill = cloneDeep(this.item);
      const selectedSkill = cloneDeep(selectedVersion);

      const version1 = `Current Version ${currentSkill.version}`;
      const version2 = `Version ${selectedVersion.version}`;

      this.comparison = {
        version1,
        version2,
        html: await skillDiffAsHtml(selectedSkill, currentSkill)
      };
    },
    async confirmDelete() {
      const confirmed = await this.$confirm({
        title: `Delete skill?`,
        message: `Are you sure you want to delete "${this.skillName}" and all its versions?\n\nThis action cannot be undone.`,
        confirm: 'Delete'
      });

      if (!confirmed) {
        return;
      }

      const lock = this.$lock();
      try {
        await this.$store.dispatch('nlp/deleteSkill', { name: this.skillName });
        this.$toast.success({
          title: 'Skill deleted',
          message: `Skill "${this.skillName}" has been deleted.`
        });
        this.$router.push('/skills');
      } catch (e) {
        const serverMessage = await this.extractErrorMessage(e);
        this.$toast.error({
          title: 'Failed to delete skill',
          message: serverMessage || 'Please try again later.'
        });
      } finally {
        lock.release();
      }
    },
    async extractErrorMessage(e) {
      try {
        const text = await e?.response?.text?.();
        if (text) return JSON.parse(text).message;
      } catch {
        // ignore parse errors
      }
      return null;
    }
  }
};
</script>

<style lang="scss" scoped>
.edit-skill {
  width: 100%;
  height: 100%;
  display: grid;
  padding: 0;
  grid-template-columns: 1fr;
  grid-template-rows: max-content minmax(0, 1fr);
  grid-gap: 0;

  .skill-name {
    color: var(--theme-primary);
  }

  > header {
    padding: 0.5rem 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-sizing: border-box;
    border-bottom: 1px solid var(--theme-on-background-dark);

    .header-left {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    h1 {
      color: var(--theme-on-surface);
      font-size: 1.5rem;
      font-weight: 700;
      margin-right: 0.25rem;

      a {
        color: var(--theme-on-surface);
        text-decoration: none;
        opacity: 0.6;

        &:hover {
          opacity: 1;
          text-decoration: underline;
        }
      }

      .separator {
        opacity: 0.4;
      }
    }

    .version-indicator {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: var(--theme-on-primary);
      font-size: 0.9rem;
      font-weight: 500;
      padding: 0.4rem 0.75rem;
      background: var(--theme-primary);
      border-radius: 4px;

      .current-label {
        color: var(--theme-on-primary);
        opacity: 0.85;
        font-size: 0.85rem;
        font-weight: 400;
        margin-left: 0.25rem;
      }
    }
  }

  .content-wrapper {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 5px;
    height: 100%;
    min-height: 0;
    overflow: hidden;
  }
}
</style>
